/**
 * Constructores de JSON-LD (schema.org).
 *
 * Todo se deriva de `organizacion.js`: no hay literales duplicados acá. El grafo
 * institucional se emite en todas las rutas y cada página agrega sus propios
 * nodos, enlazados por `@id` contra los del grafo base.
 */

import {
  SITE_URL,
  ORG_ID,
  WEBSITE_ID,
  ORGANIZACION,
  SEDES,
  ZONAS_ATENDIDAS,
  TERAPIAS,
  nombresObrasSociales,
} from './organizacion';

const sedeNode = (sede) => ({
  '@type': 'MedicalClinic',
  '@id': sede.id,
  name: sede.nombre,
  alternateName: sede.nombreCorto,
  parentOrganization: { '@id': ORG_ID },
  url: `${SITE_URL}/${sede.slug}`,
  image: sede.imagen,
  telephone: sede.telefono,
  email: ORGANIZACION.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: sede.calle,
    addressLocality: sede.localidad,
    addressRegion: sede.provincia,
    postalCode: sede.codigoPostal,
    addressCountry: sede.pais,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: sede.lat,
    longitude: sede.lng,
  },
  openingHours: ORGANIZACION.horario,
  areaServed: sede.zonas.map((z) => ({ '@type': 'City', name: z })),
  isAcceptingNewPatients: true,
});

/** Grafo institucional: se emite en todas las rutas. */
export const grafoInstitucional = () => [
  {
    '@type': ['MedicalOrganization', 'LocalBusiness'],
    '@id': ORG_ID,
    name: ORGANIZACION.nombre,
    alternateName: ORGANIZACION.alternateName,
    description: ORGANIZACION.descripcion,
    url: SITE_URL,
    logo: ORGANIZACION.logo,
    image: ORGANIZACION.logo,
    email: ORGANIZACION.email,
    telephone: SEDES[0].telefono,
    foundingDate: ORGANIZACION.fundacion,
    founder: { '@type': 'Person', name: ORGANIZACION.fundadora },
    openingHours: ORGANIZACION.horario,
    priceRange: '$$',
    medicalSpecialty: [
      'Centro Educativo Terapéutico',
      'Centro de Día',
      'Discapacidad Intelectual',
    ],
    // Las dos sedes como departamentos, no como negocios sueltos.
    department: SEDES.map((s) => ({ '@id': s.id })),
    areaServed: ZONAS_ATENDIDAS.map((z) => ({ '@type': 'City', name: z })),
    availableService: TERAPIAS.map((t) => ({
      '@type': 'MedicalTherapy',
      name: t.nombre,
      description: t.descripcion,
    })),
    paymentAccepted: nombresObrasSociales(),
    sameAs: ORGANIZACION.redes,
  },
  ...SEDES.map(sedeNode),
  {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL,
    name: ORGANIZACION.nombreCorto,
    inLanguage: 'es-AR',
    publisher: { '@id': ORG_ID },
  },
];

/** Migas de pan reales de la ruta actual. `items` = [{ nombre, path }]. */
export const breadcrumb = (items) => ({
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}${items[items.length - 1].path}#breadcrumb`,
  itemListElement: [{ nombre: 'Inicio', path: '/' }, ...items].map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.nombre,
    item: `${SITE_URL}${item.path === '/' ? '' : item.path}` || SITE_URL,
  })),
});

/** Servicio ofrecido (CET, CD, tratamientos, actividades). */
export const servicio = ({ nombre, descripcion, path, tipo = 'MedicalTherapy', ofrece = [] }) => ({
  '@type': ['Service', tipo],
  '@id': `${SITE_URL}${path}#servicio`,
  name: nombre,
  description: descripcion,
  url: `${SITE_URL}${path}`,
  provider: { '@id': ORG_ID },
  areaServed: ZONAS_ATENDIDAS.map((z) => ({ '@type': 'City', name: z })),
  availableAtOrFrom: SEDES.map((s) => ({ '@id': s.id })),
  ...(ofrece.length && {
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: nombre,
      itemListElement: ofrece.map((o) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: o.nombre, description: o.descripcion },
      })),
    },
  }),
});

/**
 * FAQPage. Google ya no da rich results de FAQ a sitios no gubernamentales, así
 * que esto no busca snippets: delimita pregunta→respuesta sin ambigüedad para
 * que un asistente pueda citar la respuesta exacta en vez de parafrasear el HTML.
 * `preguntas` = [{ pregunta, respuesta }]
 */
export const faq = (preguntas, path) => ({
  '@type': 'FAQPage',
  '@id': `${SITE_URL}${path}#faq`,
  mainEntity: preguntas.map((p) => ({
    '@type': 'Question',
    name: p.pregunta,
    acceptedAnswer: { '@type': 'Answer', text: p.respuesta },
  })),
});

/** Referencia a una sede ya definida en el grafo, como entidad principal de la página. */
export const paginaDeSede = (sede) => ({
  '@type': 'WebPage',
  '@id': `${SITE_URL}/${sede.slug}#pagina`,
  url: `${SITE_URL}/${sede.slug}`,
  isPartOf: { '@id': WEBSITE_ID },
  about: { '@id': sede.id },
  primaryImageOfPage: sede.imagen,
});
